import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import BottomBar from "../components/BottomBar";
import ThaiBEDateRangePicker from "../components/ThaiBEDateRangePicker";
import { ReactComponent as CalendarIcon } from "../assets/images/calendar.svg";
import { ReactComponent as Plus } from "../assets/images/plus.svg";
import { ReactComponent as Send } from "../assets/images/send.svg";
import { createHttpClient } from "../api/client";

const NUM = new Intl.NumberFormat("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

function pad2(n) {
    return String(n).padStart(2, "0");
}

function safeNum(x) {
    const n = Number(x);
    return Number.isFinite(n) ? n : 0;
}

// ✅ เดาชื่อประเภทอาหาร
function pickFoodTypeName(it) {
    return (
        it?.food_type?.name ||
        it?.food_type_name ||
        it?.food_type ||
        it?.name ||
        it?.type_name ||
        (it?.food_name ? String(it.food_name) : "") ||
        "ไม่ระบุ"
    );
}

// ✅ เดาน้ำหนัก
function pickWeight(it) {
    return safeNum(it?.weight ?? it?.qty ?? it?.quantity ?? it?.total_weight ?? 0);
}

export default function DonationList() {
    const nav = useNavigate();
    const http = useMemo(() => createHttpClient(), []);

    const today = useMemo(() => new Date(), []);
    const [range, setRange] = useState({ startDate: today, endDate: today });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [remainingLoading, setRemainingLoading] = useState(false);
    const [remainingError, setRemainingError] = useState("");
    const [remainingFromDistribution, setRemainingFromDistribution] = useState(null);

    // ✅ donorId จาก localStorage
    const [donorId, setDonorId] = useState(() => {
        const v = localStorage.getItem("USER_ID");
        return v ? Number(v) : null;
    });

    const [summary, setSummary] = useState({ total: 0, remaining: 0 });
    const [rows, setRows] = useState([]);

    // ✅ อ่าน USER_ID ใหม่ เผื่อเพิ่ง login
    useEffect(() => {
        const v = localStorage.getItem("USER_ID");
        setDonorId(v ? Number(v) : null);
    }, []);

    function toYMDHMS(date, hh = 0, mm = 0, ss = 0) {
        if (!date) return "";
        const d = new Date(date);
        return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(hh)}:${pad2(mm)}:${pad2(ss)}`;
    }

    function toStartDateTime(date) {
        return toYMDHMS(date, 0, 0, 0);
    }

    function toEndDateTime(date) {
        return toYMDHMS(date, 23, 59, 0); // 23:59:00 ตามตัวอย่าง API
    }

    function toLocalDT(d, end = false) {
        const x = new Date(d);
        const yyyy = x.getFullYear();
        const mm = pad2(x.getMonth() + 1);
        const dd = pad2(x.getDate());
        const t = end ? "23:59:00" : "00:00:00";
        return `${yyyy}-${mm}-${dd}T${t}`;
    }

    // 1) GET donations -> รวม items ทุก donation แล้วสรุปตามประเภทอาหาร
    useEffect(() => {
        let alive = true;

        if (!donorId) {
            setRows([]);
            setSummary({ total: 0, remaining: 0 });
            setError("ไม่พบข้อมูลผู้ใช้งาน (USER_ID) กรุณาเข้าสู่ระบบใหม่");
            return;
        }

        const start_date = toStartDateTime(range.startDate);
        const end_date = toEndDateTime(range.endDate);

        (async () => {
            setLoading(true);
            setError("");

            try {
                const res = await http.get(`/api/donations/getDonation/user/${donorId}`, {
                    params: { page: 1, limit: 100, start_date, end_date },
                });

                if (!alive) return;

                const list =
                    (Array.isArray(res?.data?.data) && res.data.data) ||
                    (Array.isArray(res?.data?.donations) && res.data.donations) ||
                    (Array.isArray(res?.data) && res.data) ||
                    [];

                const getDonationItems = (d) =>
                    (Array.isArray(d?.items) && d.items) ||
                    (Array.isArray(d?.donation_items) && d.donation_items) ||
                    (Array.isArray(d?.data?.items) && d.data.items) ||
                    [];

                const allItems = list.flatMap(getDonationItems);

                const bucket = new Map();
                let computedTotal = 0;

                for (const it of allItems) {
                    const name = pickFoodTypeName(it);
                    const w = pickWeight(it);
                    if (!name || w <= 0) continue;

                    computedTotal += w;
                    bucket.set(name, (bucket.get(name) || 0) + w);
                }

                const tableRows = Array.from(bucket.entries())
                    .map(([name, qty]) => ({ name, qty }))
                    .sort((a, b) => b.qty - a.qty);

                setRows(tableRows);
                setSummary((prev) => ({ ...prev, total: computedTotal }));
            } catch (e) {
                console.error("GET donations error:", e);
                if (!alive) return;
                setError("โหลดข้อมูลรายการอาหารบริจาคไม่สำเร็จ");
                setRows([]);
                setSummary((prev) => ({ ...prev, total: 0 }));
            } finally {
                if (alive) setLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [http, donorId, range.startDate, range.endDate]);

    // 2) GET remaining จาก distributions (เอาแค่ data[0])
    // 2) GET remaining จาก distributions (เอาแค่ data[0])
    useEffect(() => {
        let alive = true;

        if (!donorId) return;

        const start_date = toStartDateTime(range.startDate);
        const end_date = toEndDateTime(range.endDate);

        (async () => {
            setRemainingLoading(true);
            setRemainingError("");

            try {
                const res = await http.get(`/api/distribution/getByUserIdDistributions/${donorId}`, {
                    params: { start_date, end_date, page: 1, limit: 10 },
                });

                if (!alive) return;

                const list = Array.isArray(res?.data?.data) ? res.data.data : [];
                const first = list[0] || null; // ✅ เอา index แรกเท่านั้น

                // ✅ ถ้ามีข้อมูล: ใช้ remaining_weight
                // ✅ ถ้าไม่มีข้อมูล: ตั้งเป็น null เพื่อให้ UI fallback ไปใช้ summary.total
                setRemainingFromDistribution(first ? safeNum(first?.remaining_weight) : null);
            } catch (e) {
                console.error("GET distributions remaining error:", e);
                if (!alive) return;

                setRemainingError("โหลดยอดคงเหลือไม่สำเร็จ");
                // ✅ error ก็ fallback ไปใช้ summary.total
                setRemainingFromDistribution(null);
            } finally {
                if (alive) setRemainingLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [http, donorId, range.startDate, range.endDate]);

    const overlayLoading = loading || remainingLoading;

    const remainingDisplay =
        remainingFromDistribution === null ? summary.total : remainingFromDistribution;

    return (
        <div className="min-h-screen bg-white flex flex-col pb-40 items-center">
            <div className="w-full">
                <Header />
            </div>

            {/* Loading overlay */}
            {overlayLoading && (
                <div className="fixed inset-0 z-[999] bg-white/70 backdrop-blur-[2px] grid place-items-center">
                    <div className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 px-5 py-4 shadow">
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
                        <div className="text-[14px] text-slate-700">กำลังโหลดข้อมูล…</div>
                    </div>
                </div>
            )}

            <div className="px-4 sm:px-6 w-full max-w-[560px]">
                {/* Title */}
                <div className="text-xl font-bold text-[#407865]">อาหารบริจาค</div>

                {/* Errors */}
                {error ? (
                    <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                        {error}
                    </div>
                ) : null}

                {remainingError ? (
                    <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                        {remainingError}
                    </div>
                ) : null}

                {/* Date range (1 day) */}
                <div className="mt-3 w-fit rounded-xl bg-white border-2 border-[#9CA3AF] px-3 py-2 shadow-sm">
                    <ThaiBEDateRangePicker
                        modeKey="1day"
                        today={today}
                        Icon={CalendarIcon}
                        startDate={range.startDate}
                        endDate={range.endDate}
                        onChange={({ startDate, endDate }) => {
                            const d = startDate || endDate || new Date();
                            setRange({ startDate: d, endDate: d }); // 1 วัน
                        }}
                    />
                </div>

                {/* Actions */}
                <div className="mt-5 grid grid-cols-2 gap-4">
                    <ActionCard
                        title="รับอาหารบริจาค"
                        icon={<Plus className="w-10 h-10" />}
                        onClick={() => {
                            const start = toLocalDT(range.startDate, false);
                            const end = toLocalDT(range.endDate, true);
                            nav(`/receive_donation?start_date=${encodeURIComponent(start)}&end_date=${encodeURIComponent(end)}`);
                        }}
                    />
                    <ActionCard
                        title="ส่งต่ออาหารบริจาค"
                        icon={<Send className="w-10 h-10" />}
                        onClick={() => {
                            const start = toLocalDT(range.startDate, false);
                            const end = toLocalDT(range.endDate, true);
                            nav(`/send_donation?start_date=${encodeURIComponent(start)}&end_date=${encodeURIComponent(end)}`);
                        }}
                    />
                </div>

                {/* Section title */}
                <div className="mt-6 text-[16px] font-bold text-slate-900">รายการอาหารบริจาค</div>

                {/* Summary card */}
                <div className="mt-3 rounded-2xl border border-orange-300 bg-white px-5 py-4">
                    <div className="grid grid-cols-3 gap-4 justify-between px-2">
                        <div className="text-center w-full flex flex-col items-center justify-center text-nowrap whitespace-nowrap">
                            <div className="text-sm font-medium text-slate-600">รวมทั้งหมด</div>
                            <div className="flex flex-row items-end justify-center gap-2 mt-1">
                                <div className="mt-1 text-[26px] font-extrabold text-slate-900">{NUM.format(summary.total)}</div>
                                <div className="text-[13px] text-slate-500 mb-1">กก.</div>
                            </div>
                        </div>

                        <div className="flex justify-center">
                            <div className="h-12 w-[3px] rounded-full bg-[#9CA3AF]" />
                        </div>

                        <div className="text-center w-full flex flex-col items-center justify-center text-nowrap whitespace-nowrap">
                            <div className="text-sm font-medium text-slate-600">ยอดคงเหลือ</div>
                            <div className="flex flex-row items-end justify-center gap-2 mt-1">
                                <div className="mt-1 text-[26px] font-extrabold text-[#ED9C50]">{NUM.format(remainingDisplay)}</div>
                                <div className="text-[13px] text-slate-500 mb-1">กก.</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="mt-4 overflow-hidden rounded-xl border border-[#F3F0DC]">
                    <table className="w-full text-left text-[14px]">
                        <thead className="bg-[#F3F0DC]">
                            <tr>
                                <th className="px-4 py-2 font-medium text-slate-700">ประเภทอาหาร</th>
                                <th className="px-4 py-2 font-medium text-slate-700 text-right">ปริมาณ(กิโลกรัม)</th>
                            </tr>
                        </thead>

                        <tbody>
                            {rows.map((r, idx) => (
                                <tr key={`${r.name}-${idx}`} className="bg-white border-t border-[#F3F0DC]">
                                    <td className="px-4 py-2 text-slate-800">{r.name}</td>
                                    <td className="px-4 py-2 text-right font-semibold text-black">{NUM.format(r.qty)}</td>
                                </tr>
                            ))}

                            {!overlayLoading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={2} className="px-4 py-8 text-center text-slate-500">
                                        ไม่พบข้อมูล
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <BottomBar />
        </div>
    );
}

function ActionCard({ title, icon, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={[
                "rounded-2xl border-2 border-[#84DA8A] bg-[#E4FBE9]",
                "px-4 py-6 text-[#2F6B55] shadow-sm",
                "flex flex-col items-center justify-center gap-3",
                "hover:brightness-[0.99] active:scale-[0.99] transition",
            ].join(" ")}
        >
            <div className="grid place-items-center">{icon}</div>
            <div className="text-sm font-bold">{title}</div>
        </button>
    );
}
